import { useMutation, type UseMutationOptions, type UseMutationResult } from '@tanstack/react-query'
import { isProblemError } from '@volee/api-client'

import { askForPassword } from '../services/password-check.ts'

// What a tutore decides for a child asks their password again (spec #278, #301): the act is tried, and a
// refusal for that asks the person and tries once more.

/** The person closed the question: the act was not done, and there is nothing to report about it. */
export class PasswordCheckCancelled extends Error {
  constructor() {
    super('The password was not given')
    this.name = 'PasswordCheckCancelled'
  }
}

function isPasswordCheckCancelled(error: unknown): boolean {
  return error instanceof PasswordCheckCancelled
}

function asksForThePassword(error: unknown): boolean {
  return isProblemError(error) && error.code === 'auth.password_verification_required'
}

/** Runs `act`; if the API wants the password again, asks for it and runs `act` once more. Passes everything
 *  else through, and a second refusal too: a window too short to hold is the server's to widen. */
export async function withRecentPassword<T>(act: () => Promise<T>): Promise<T> {
  try {
    return await act()
  } catch (error) {
    if (!asksForThePassword(error)) throw error
    if (!(await askForPassword())) throw new PasswordCheckCancelled()
    return act()
  }
}

export interface AskingThePasswordOptions<TData, TVariables> extends UseMutationOptions<
  TData,
  Error,
  TVariables
> {
  /** The question was closed unanswered: the act was not done, and whatever it opened can close. */
  readonly onCancelled?: () => void
}

type CallOptions<TData, TVariables> = Parameters<
  UseMutationResult<TData, Error, TVariables>['mutate']
>[1]

/** The `onError` given to one `mutate()` call does not hear a question closed unanswered either. */
function withoutCancelled<TData, TVariables>(
  callOptions: CallOptions<TData, TVariables>,
): CallOptions<TData, TVariables> {
  const onError = callOptions?.onError
  if (callOptions === undefined || onError === undefined) return callOptions
  return {
    ...callOptions,
    onError: (...args) => {
      if (!isPasswordCheckCancelled(args[0])) onError(...args)
    },
  }
}

/** `useMutation` for an act that may go through `withRecentPassword`: a question closed unanswered is no
 *  failure, so no `onError` (hook or call), no `error`, and the mutation reads as idle. `onCancelled`
 *  is for what the act had opened. */
export function useMutationAskingThePassword<TData, TVariables = void>({
  onError,
  onCancelled,
  ...options
}: AskingThePasswordOptions<TData, TVariables>): UseMutationResult<TData, Error, TVariables> {
  const mutation = useMutation<TData, Error, TVariables>({
    ...options,
    onError: (...args) => {
      if (isPasswordCheckCancelled(args[0])) onCancelled?.()
      else onError?.(...args)
    },
  })
  const mutate: typeof mutation.mutate = (variables, callOptions) => {
    mutation.mutate(variables, withoutCancelled(callOptions))
  }
  if (!isPasswordCheckCancelled(mutation.error)) return { ...mutation, mutate }
  return {
    ...mutation,
    mutate,
    data: undefined,
    variables: undefined,
    error: null,
    isError: false,
    isIdle: true,
    isPending: false,
    isSuccess: false,
    status: 'idle',
  }
}
